import type { ListBoxItemProps, SelectProps } from "react-aria-components";
import {
  Autocomplete,
  Button,
  FieldError,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  Select as SelectBase,
  SelectValue,
  Text,
  useFilter,
} from "react-aria-components";
import { css, cx, type RecipeVariantProps } from "../../../../styled-system/css";
import { FaChevronDown } from "react-icons/fa6";
import { inputClassNames, popoverStyles } from "../styles/styles";
import { SearchInput } from "./TextField";
import { type ReactNode } from "react";

interface Props<T extends object> extends Omit<SelectProps<T>, "children"> {
  className?: string;
  label?: ReactNode;
  description?: string;
  errorMessage?: string;
  items?: ReadonlyArray<T>;
  isSearchable?: boolean | "auto";
  children?: React.ReactNode | ((item: T) => React.ReactNode);
  /** Formily seems to mess up children, so this is a workaround to allow it to pass custom renderer */
  itemRenderer?: (item: T) => React.ReactNode;
  variant?: Exclude<RecipeVariantProps<typeof inputClassNames>, undefined>["variant"];
}

export function Select<T extends object>({
  label,
  description,
  errorMessage,
  children,
  items,
  itemRenderer,
  variant,
  isSearchable = "auto",
  ...props
}: Props<T>) {
  const classes = inputClassNames({ variant });
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { contains } = useFilter({ sensitivity: "base" });
  const isSearchEnabled = isSearchable === "auto" ? items && items.length > 10 : isSearchable;
  return (
    <SelectBase {...props} className={cx(classes.fieldWrapper, props.className)}>
      {label && <Label className={classes.label}>{label}</Label>}
      <Button className={cx(classes.inputBox, css({ paddingY: 0 }))}>
        <SelectValue
          className={cx(
            classes.input,
            css({
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }),
          )}
        />
        <span aria-hidden="true" className={css({ fontSize: "xxsmall" })}>
          <FaChevronDown />
        </span>
      </Button>
      {description && <Text slot="description">{description}</Text>}
      {errorMessage && <FieldError className={classes.error}>{errorMessage}</FieldError>}
      <Popover
        className={cx(
          popoverStyles,
          css({
            width: "var(--trigger-width)",
            paddingY: "xsmall",
          }),
        )}
        shouldCloseOnInteractOutside={(element) => {
          if (element.className.includes("modal")) {
            return false;
          }
          return true;
        }}
      >
        <Autocomplete filter={contains}>
          {isSearchEnabled && (
            <SearchInput
              variant="inline"
              aria-label={"Search"}
              autoFocus
              className={css({ marginY: "xsmall", marginX: "small" })}
            />
          )}
          <ListBox
            className={css({
              scrollbarColor: "{colors.lightgrey} transparent",
              scrollbarGutter: "stable both-edges",
              scrollbarWidth: "thin",
              overflowY: "auto",
            })}
            items={items}
          >
            {children ?? itemRenderer ?? defaultRenderer}
          </ListBox>
        </Autocomplete>
      </Popover>
    </SelectBase>
  );

  function defaultRenderer(item: object) {
    if (
      "label" in item &&
      "value" in item &&
      (typeof item.value === "string" || typeof item.value === "number" || typeof item.value === "boolean") &&
      typeof item.label === "string"
    ) {
      // Booleans seem to work fine as values, but not officially supported
      return (
        <SelectItem id={item.value as string} textValue={item.label}>
          {item.label || "%missing_label%"}
        </SelectItem>
      );
    } else {
      throw new Error("You need a custom renderer");
    }
  }
}

export function SelectItem({ className, ...props }: ListBoxItemProps & { className?: string }) {
  return (
    <ListBoxItem
      {...props}
      className={cx(
        css({
          cursor: "pointer",

          paddingY: "xsmall",
          paddingX: "medium",
          color: { base: "darkblue/80", _focus: "darkblue", _hover: "darkblue" },
          fontSize: "small",
          backgroundColor: { base: "white", _focus: "black/10", _hover: "black/10" },
          _selected: {
            color: { base: "white", _focus: "white", _hover: "white" },
            backgroundColor: { base: "trellisPalette.4/80", _focus: "trellisPalette.4", _hover: "trellisPalette.4" },
          },
          _focusVisible: { outline: "1px solid {colors.bootstrapBlue}" },
        }),
        className,
      )}
    />
  );
}
